/** Renders compact attunement tree clusters for the overview grid in AttunementTreeScene. */
import Phaser from 'phaser';
import type { LayoutTier } from './TreeLayoutEngine';
import { crispGraphics, crispText, CRISP_SCALE } from './CrispUtil';
import { Color, Hex, Font, getTierColor, CLUSTER_BAR_BG_ALPHA, CLUSTER_BAR_FILL_ALPHA, CLUSTER_SELECTION_ALPHA } from './Theme';
import { ConfigLoader } from '../systems/ConfigLoader';

export interface TreeClusterConfig {
  treeName: string;
  currentTier: LayoutTier;
  treeXp: number;
  isSelected: boolean;
}

// Cluster dimensions (logical pixels)
const CLUSTER_W = 120;
const CLUSTER_H = 100;

/**
 * Renders a compact tree cluster for the overview grid.
 * Each cluster shows a tree icon, name label, and XP bar.
 */
export class TreeClusterRenderer {
  private scene: Phaser.Scene;
  private objects: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Draw a compact tree cluster centered at (cx, cy).
   * Returns a Zone for click detection.
   */
  render(cx: number, cy: number, config: TreeClusterConfig): Phaser.GameObjects.Zone {
    const { treeName, currentTier, treeXp, isSelected } = config;
    const S = CRISP_SCALE;

    const g = crispGraphics(this.scene);
    this.objects.push(g);

    // Selection highlight
    if (isSelected) {
      g.lineStyle(2 * S, Color.treeNodeEquipped, CLUSTER_SELECTION_ALPHA);
      g.strokeRoundedRect(
        (cx - CLUSTER_W / 2) * S, (cy - CLUSTER_H / 2 + 10) * S,
        CLUSTER_W * S, (CLUSTER_H - 10) * S, 6 * S,
      );
    }

    // Tree icon
    const icon = this.scene.add.image(cx, cy - 2, 'attunement-icons', `tree-icon-${treeName}`)
      .setDisplaySize(40, 40)
      .setOrigin(0.5);
    this.objects.push(icon);

    // Tree name label
    const nameText = crispText(this.scene, cx, cy - 26,
      treeName.toUpperCase(), {
        fontFamily: Font.family, fontSize: Font.sizeXs, color: Hex.textPrimary,
      } as Phaser.Types.GameObjects.Text.TextStyle,
    ).setOrigin(0.5, 1);
    this.objects.push(nameText);

    // XP bar
    const barWidth = 60;
    const barHeight = 4;
    const barX = cx - barWidth / 2;
    const barY = cy + 24;

    // Next threshold approximation for XP bar
    const nextThreshold = this.getNextThreshold(currentTier);

    // Bar background
    g.fillStyle(Color.treeBranch, CLUSTER_BAR_BG_ALPHA);
    g.fillRect(barX * S, barY * S, barWidth * S, barHeight * S);

    // Bar fill
    if (nextThreshold > 0) {
      const fillPct = Math.min(treeXp / nextThreshold, 1);
      const fillColor = getTierColor(currentTier);
      g.fillStyle(fillColor, CLUSTER_BAR_FILL_ALPHA);
      g.fillRect(barX * S, barY * S, barWidth * fillPct * S, barHeight * S);
    } else {
      g.fillStyle(Color.treeTierApotheosis, CLUSTER_BAR_FILL_ALPHA);
      g.fillRect(barX * S, barY * S, barWidth * S, barHeight * S);
    }

    // XP text
    const displayXp = Math.floor(treeXp);
    const xpLabel = nextThreshold > 0 ? `${displayXp} / ${nextThreshold}` : `${displayXp} MAX`;
    const xpText = crispText(this.scene, cx, barY + barHeight + 4, xpLabel, {
      fontFamily: Font.family, fontSize: Font.sizeXs, color: Hex.textMuted,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5, 0);
    this.objects.push(xpText);

    // Click zone
    const zone = this.scene.add.zone(cx, cy, CLUSTER_W, CLUSTER_H)
      .setInteractive({ useHandCursor: true });
    this.objects.push(zone);

    return zone;
  }

  /** Destroy all rendered cluster objects. */
  clear(): void {
    for (const obj of this.objects) obj.destroy();
    this.objects = [];
  }

  /** Approximate next XP threshold for bar display. */
  private getNextThreshold(currentTier: LayoutTier): number {
    const b = ConfigLoader.masteryTierBoundaries;
    switch (currentTier) {
      case 'foundation': return b[0];
      case 'threshold': return b[1];
      case 'transcendent': return b[2];
      case 'apotheosis': return 0; // maxed
    }
  }
}
